// src/components/FlappyBirdGame.js
import React, { useState, useEffect, useRef } from 'react';

const FlappyBirdGame = ({ mode, roomId, socket, opponentId, userId, onGameEnd, mapData = null }) => {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [opponentScore, setOpponentScore] = useState(0);
  const [opponentAlive, setOpponentAlive] = useState(true);
  const [opponentY, setOpponentY] = useState(250);
  const [opponentFalling, setOpponentFalling] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState(null);
  const birdRef = useRef({ y: 250, velocity: 0, angle: 0 });
  const pipesRef = useRef([]);
  const animationIdRef = useRef();
  const intervalIdRef = useRef();
  const localDeadRef = useRef(false);
  const mapMovingRef = useRef(true);
  const countdownStartedRef = useRef(false);
  const countdownRef = useRef(null);

  useEffect(() => {
    countdownRef.current = countdown;
  }, [countdown]);

  const opponentYRef = useRef(opponentY);
  const opponentAliveRef = useRef(opponentAlive);
  const opponentScoreRef = useRef(opponentScore);
  const scoreRef = useRef(score);
  const opponentFallingRef = useRef(opponentFalling);

  useEffect(() => {
    opponentYRef.current = opponentY;
  }, [opponentY]);
  useEffect(() => {
    opponentAliveRef.current = opponentAlive;
  }, [opponentAlive]);
  useEffect(() => {
    opponentScoreRef.current = opponentScore;
  }, [opponentScore]);
  useEffect(() => {
    scoreRef.current = score;
  }, [score]);
  useEffect(() => {
    opponentFallingRef.current = opponentFalling;
  }, [opponentFalling]);

  const GRAVITY = 0.2;
  const JUMP = -4.5;
  const PIPE_WIDTH = 60;
  const PIPE_GAP = 120;
  const PIPE_SPACING = 200;
  const BIRD_SIZE = 24;

  const initGame = () => {
    birdRef.current = { y: 250, velocity: 0, angle: 0 };
    if (mode === 'multi' && mapData && Array.isArray(mapData)) {
      pipesRef.current = mapData.map(pipe => ({ ...pipe }));
    } else {
      pipesRef.current = [];
      for (let i = 0; i < 3; i++) {
        pipesRef.current.push({
          x: 400 + i * PIPE_SPACING,
          gapY: 100 + Math.random() * 200
        });
      }
    }
    setScore(0);
    localDeadRef.current = false;
    mapMovingRef.current = true;
    setOpponentFalling(false);
    setGameActive(false);
    countdownStartedRef.current = false;
    setCountdown(null);
    countdownRef.current = null;
  };

  // 倒计时从4开始，确保显示4,3,2,1
  useEffect(() => {
    if (mode === 'multi' && !gameActive && !countdownStartedRef.current && !localDeadRef.current) {
      countdownStartedRef.current = true;
      let count = 4;
      setCountdown(count);
      const timer = setInterval(() => {
        count -= 1;
        if (count === 0) {
          clearInterval(timer);
          setCountdown(null);
          setGameActive(true);
        } else {
          setCountdown(count);
        }
      }, 1000);
      return () => {
        clearInterval(timer);
        countdownStartedRef.current = false;
      };
    }
  }, [mode, gameActive]);

  useEffect(() => {
    initGame();
    return () => {
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    };
  }, [mode, mapData]);

  const checkCollision = (birdY, pipes) => {
    if (birdY <= 0 || birdY + BIRD_SIZE >= 600) return true;
    for (let pipe of pipes) {
      if (100 + BIRD_SIZE > pipe.x && 100 < pipe.x + PIPE_WIDTH) {
        if (birdY < pipe.gapY || birdY + BIRD_SIZE > pipe.gapY + PIPE_GAP) return true;
      }
    }
    return false;
  };

  const generateNewGapY = () => 100 + Math.random() * 200;

  const updateGame = () => {
    if (!gameActive) return;
    if (localDeadRef.current) {
      if (mapMovingRef.current && opponentAliveRef.current && !opponentFallingRef.current) {
        for (let pipe of pipesRef.current) pipe.x -= 2;
        if (mode !== 'multi' && pipesRef.current[0] && pipesRef.current[0].x + PIPE_WIDTH < 0) {
          pipesRef.current.shift();
          pipesRef.current.push({
            x: pipesRef.current[pipesRef.current.length-1].x + PIPE_SPACING,
            gapY: generateNewGapY()
          });
        }
      }
      return;
    }
    birdRef.current.velocity += GRAVITY;
    birdRef.current.y += birdRef.current.velocity;
    birdRef.current.angle = Math.min(Math.max(birdRef.current.velocity * 0.15, -0.5), 0.8);
    for (let pipe of pipesRef.current) pipe.x -= 2;
    if (pipesRef.current[0] && pipesRef.current[0].x + PIPE_WIDTH < 0) {
      pipesRef.current.shift();
      setScore(s => s + 1);
      if (mode !== 'multi') {
        pipesRef.current.push({
          x: pipesRef.current[pipesRef.current.length-1].x + PIPE_SPACING,
          gapY: generateNewGapY()
        });
      }
    }
    if (checkCollision(birdRef.current.y, pipesRef.current)) {
      localDeadRef.current = true;
      if (mode === 'multi' && socket) {
        socket.emit('flappy_game_over', { room_id: roomId, token: localStorage.getItem('memo_token') });
      } else if (mode === 'solo') {
        if (onGameEnd) onGameEnd(scoreRef.current);
      }
      return;
    }
    if (mode === 'multi' && socket && gameActive && !localDeadRef.current) {
      socket.emit('flappy_update_state', {
        room_id: roomId,
        token: localStorage.getItem('memo_token'),
        score: scoreRef.current,
        alive: true,
        y: birdRef.current.y
      });
    }
  };

  const jump = () => {
    if (!gameActive || localDeadRef.current) return;
    birdRef.current.velocity = JUMP;
    birdRef.current.angle = -0.5;
  };

  useEffect(() => {
    if (!opponentFalling) return;
    const fallInterval = setInterval(() => {
      if (opponentFallingRef.current) {
        setOpponentY(prev => {
          const next = prev + 4;
          if (next + BIRD_SIZE >= 600) {
            setOpponentFalling(false);
            opponentFallingRef.current = false;
            clearInterval(fallInterval);
            return 600 - BIRD_SIZE;
          }
          return next;
        });
      } else {
        clearInterval(fallInterval);
      }
    }, 16);
    return () => clearInterval(fallInterval);
  }, [opponentFalling]);

  const drawBird = (ctx, x, y, color, angle, opacity = 1) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.translate(x + BIRD_SIZE/2, y + BIRD_SIZE/2);
    ctx.rotate(angle);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(0, 0, BIRD_SIZE/2, BIRD_SIZE/2 * 0.8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.arc(BIRD_SIZE/2 - 6, -3, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#f39c12';
    ctx.beginPath();
    ctx.moveTo(BIRD_SIZE/2 - 2, -3);
    ctx.lineTo(BIRD_SIZE/2 + 4, -3);
    ctx.lineTo(BIRD_SIZE/2, 0);
    ctx.fill();
    ctx.restore();
  };

  const drawPipe = (ctx, x, gapY, width, gapHeight) => {
    ctx.fillStyle = '#2ecc71';
    ctx.fillRect(x, 0, width, gapY);
    ctx.fillRect(x, gapY + gapHeight, width, 600 - gapY - gapHeight);
    ctx.fillStyle = '#27ae60';
    ctx.fillRect(x - 5, gapY - 20, width + 10, 20);
    ctx.fillRect(x - 5, gapY + gapHeight, width + 10, 20);
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = 800;
    canvas.height = 600;
    ctx.clearRect(0, 0, 800, 600);
    const grad = ctx.createLinearGradient(0, 0, 0, 600);
    grad.addColorStop(0, '#4ec0ca');
    grad.addColorStop(1, '#2c3e50');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 800, 600);
    for (let pipe of pipesRef.current) drawPipe(ctx, pipe.x, pipe.gapY, PIPE_WIDTH, PIPE_GAP);
    if (!localDeadRef.current) drawBird(ctx, 100, birdRef.current.y, '#f1c40f', birdRef.current.angle);
    if (mode === 'multi' && (opponentAliveRef.current || opponentFallingRef.current)) {
      ctx.globalAlpha = 0.6;
      drawBird(ctx, 100, opponentYRef.current, '#e74c3c', 0);
      ctx.globalAlpha = 1;
    }
    ctx.font = 'bold 28px "Press Start 2P", monospace';
    ctx.fillStyle = '#fff';
    ctx.fillText(`Score: ${scoreRef.current}`, 20, 60);
    if (mode === 'multi') ctx.fillText(`Opp: ${opponentScoreRef.current}`, 20, 110);
    
    const cd = countdownRef.current;
    if (cd !== null && cd > 0) {
      ctx.save();
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.arc(400, 300, 70, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(400, 300, 68, 0, Math.PI * 2);
      ctx.fillStyle = '#f39c12';
      ctx.fill();
      ctx.font = 'bold 80px "Press Start 2P", monospace';
      ctx.fillStyle = '#fff';
      ctx.shadowBlur = 4;
      ctx.shadowColor = 'black';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(cd, 400, 300);
      ctx.restore();
    }
    animationIdRef.current = requestAnimationFrame(draw);
  };

  useEffect(() => {
    const handleJumpGlobal = (e) => {
      if (e.type === 'touchstart') e.preventDefault();
      jump();
    };
    window.addEventListener('click', handleJumpGlobal);
    window.addEventListener('touchstart', handleJumpGlobal, { passive: false });
    return () => {
      window.removeEventListener('click', handleJumpGlobal);
      window.removeEventListener('touchstart', handleJumpGlobal);
    };
  }, [jump]);

  useEffect(() => {
    intervalIdRef.current = setInterval(updateGame, 1000 / 60);
    draw();
    return () => {
      clearInterval(intervalIdRef.current);
      cancelAnimationFrame(animationIdRef.current);
    };
  }, [gameActive]);

  useEffect(() => {
    if (mode !== 'multi' || !socket) return;
    const handleOpponent = (data) => {
      if (opponentFallingRef.current) return;
      setOpponentScore(data.score);
      setOpponentAlive(data.alive);
      if (data.y !== undefined) setOpponentY(data.y);
    };
    const handleOpponentDead = (data) => {
      if (data.dead_id === opponentId) {
        setOpponentAlive(false);
        setOpponentFalling(true);
      }
    };
    const handleGameResult = (data) => {
      setResultData(data);
      setShowResult(true);
      setGameActive(false);
      mapMovingRef.current = false;
    };
    socket.on('flappy_opponent_state', handleOpponent);
    socket.on('flappy_opponent_dead', handleOpponentDead);
    socket.on('flappy_game_result', handleGameResult);
    return () => {
      socket.off('flappy_opponent_state');
      socket.off('flappy_opponent_dead');
      socket.off('flappy_game_result');
    };
  }, [mode, socket, opponentId]);

  if (showResult && resultData) {
    const isWinner = resultData.winner_id === userId;
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <h2>{isWinner ? '🎉 胜利！' : '💀 失败'}</h2>
          <p>你的得分: {resultData.scores[userId]}</p>
          <p>对手得分: {resultData.scores[opponentId]}</p>
          <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
            <button
              onClick={() => window.location.reload()}
              onTouchEnd={(e) => { e.stopPropagation(); window.location.reload(); }}
              style={styles.button}
            >
              再来一局
            </button>
            <button
              onClick={() => window.location.href = '/game-center'}
              onTouchEnd={(e) => { e.stopPropagation(); window.location.href = '/game-center'; }}
              style={styles.button}
            >
              返回大厅
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <canvas ref={canvasRef} style={{ width: '100%', maxWidth: '800px', height: 'auto', border: '2px solid #f39c12', borderRadius: '16px', cursor: 'pointer', touchAction: 'none', display: 'block', margin: '0 auto' }} />;
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    backdropFilter: 'blur(5px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'rgba(30,30,40,0.95)',
    borderRadius: '20px',
    padding: '30px',
    textAlign: 'center',
    maxWidth: '400px',
    width: '80%',
    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: 'white'
  },
  button: {
    background: '#f39c12',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '30px',
    color: 'white',
    fontSize: '16px',
    cursor: 'pointer',
    touchAction: 'manipulation'
  }
};

export default FlappyBirdGame;